// Customer MID API client for the marketplace customer embed — TD-384 T4
// ("Refer & Save", migration 096), amended by the account-area redesign
// Task 19 (F9; ruling S3): the claim is now a LINK REQUEST.
//
// Wire shape mirrors the Go model exactly (project rule §39):
//   model: internal/marketplace/model/merchant_mid.go (MerchantMID)
//   handler: internal/marketplace/handler/customer_handler_mid.go
//     - HandleMyMIDsList (GET  /api/v1/marketplace/me/mids) — own rows, no
//       status filter (the merchant sees every claim they ever made; the
//       lifecycle badges are the panel's job)
//     - HandleMyMIDClaim  (POST /api/v1/marketplace/me/mids) — the link
//       request; answers a STATUS ENVELOPE, never a row
//
// Both handlers emit `{"data": <payload>}` — after embedApiFetch's .data
// unwrap, listMyMIDs() returns the array directly and claimMID() returns the
// envelope payload (the store-api.ts convention).
//
// The merchant has NO mutating route beyond the initial request — verify/
// close/reject are operator-only (the credit-theft control: no MID reaches a
// redeemable state without a named operator). So unlike store-api.ts there is
// no If-Match client here at all: nothing in this surface ever PATCHes an
// existing MID.
//
// BINDING client constraint (T3 minor #1, carried in the task dispatch):
// OMIT empty optional fields in the request payload — never send
// `store_id: ""`. The backend DOES normalize a blank store_id to absent
// (blankToNil at both MID boundaries), but the OMIT stays binding on the
// client: absent and cleared are the same request, the trimmed payload is
// what the wire contract documents, and a defense must not lean on the
// far side of the wire being kind (a future backend regression would then
// surface here as a 500 instead of a no-op).

import { embedApiFetch } from "../../embed-shared/api-client";

/**
 * One processor MID held by the authenticated customer. Mirrors Go
 * model.MerchantMID exactly (§39): pointer fields are `omitempty` on the wire,
 * so an unset optional arrives as an ABSENT key — `undefined` in TS, never a
 * value to coerce. Dates (opened_on/closed_on) are YYYY-MM-DD strings;
 * verified_at is an RFC3339 timestamp; verified_by_user_id is the operator
 * who verified the claim (present only on active/closed rows).
 *
 * The requested_* fields (migration 146, Task 19) are the SELF-DECLARED
 * location the customer submitted with a link request when no store was
 * chosen — the admin work item. They are informational on this surface: the
 * customer's own list shows the linked store, not the request snapshot.
 */
export interface MerchantMIDWire {
  id: string; // UUID
  customer_id: number;
  store_id?: string; // *string, omitempty — optional store association
  /**
   * Processor-assigned account number. TEXT, never numeric: processor MIDs
   * are digit strings and may carry leading zeros (design §3). Render and
   * submit it as a string everywhere — never Number() it.
   */
  mid_number: string;
  processor_name?: string; // *string, omitempty
  dba_name?: string; // *string, omitempty
  status: string; // pending | active | closed | rejected
  opened_on?: string; // *string, omitempty (YYYY-MM-DD)
  closed_on?: string; // *string, omitempty (YYYY-MM-DD; the 12-month tail clock)
  verified_at?: string; // *time.Time, omitempty (RFC3339)
  verified_by_user_id?: number; // *int64, omitempty (storefront_users.id)
  requested_store_name?: string; // *string, omitempty (migration 146 snapshot)
  requested_line1?: string; // *string, omitempty
  requested_city?: string; // *string, omitempty
  requested_state?: string; // *string, omitempty
  requested_postal_code?: string; // *string, omitempty
  version: number; // rule #10 OCC token (informational on this surface)
}

/** Request body for POST /api/v1/marketplace/me/mids. Only `mid_number` is
 *  required; every other field is optional and OMITTED when blank — an
 *  absent key means "not provided", and the client never sends "" (see the
 *  file-header constraint). The server additionally requires store name +
 *  ZIP when no `store_id` is given (422) — the panel validates that
 *  client-side first. */
export interface ClaimMIDRequest {
  mid_number: string;
  store_id?: string; // UUID of one of the caller's own stores
  processor_name?: string;
  dba_name?: string;
  opened_on?: string; // YYYY-MM-DD
  // The self-declared location (migration 146) — required name + ZIP when
  // no store_id (server-enforced; the dialog prefills them from the chosen
  // store, O10).
  requested_store_name?: string;
  requested_line1?: string;
  requested_city?: string;
  requested_state?: string;
  requested_postal_code?: string;
}

/**
 * What the link request answered. The endpoint NEVER returns a row:
 *   - "already_linked": the number matches the caller's OWN live ACTIVE row
 *     (the Aldelo pre-set meeting its owner) — nothing was created;
 *   - "queued": the request joined the review queue — either a fresh row
 *     was created, or the caller's own pending request is still open, or
 *     the number is another customer's (existence is never disclosed —
 *     OWASP A01). The panel re-reads the list to learn which.
 */
export type ClaimMIDResult = { outcome: "already_linked" } | { outcome: "queued" };

const MIDS_PATH = "/api/v1/marketplace/me/mids";

/**
 * List every MID claim the authenticated customer ever made (pending through
 * closed). GET /api/v1/marketplace/me/mids → CustomerMID[] directly after
 * the .data unwrap; an empty list arrives as data:[] and stays [].
 */
export async function listMyMIDs(): Promise<MerchantMIDWire[]> {
  const result = await embedApiFetch<MerchantMIDWire[]>(MIDS_PATH);
  return result ?? [];
}

/**
 * Submit a merchant-service link request (Task 19, ruling S3).
 *
 * POST /api/v1/marketplace/me/mids → {"data":{"already_linked":true}} or
 * {"data":{"queued":true}} — parsed into the ClaimMIDResult union.
 *
 * Blank optionals are OMITTED from the body entirely (never sent as "" —
 * the backend boundary does not normalize empty strings to NULL; a blank
 * store_id as "" would fail the server's UUID handling instead of meaning
 * "no store"). mid_number passes through verbatim as TEXT — leading zeros
 * are real (design §3).
 *
 * Throws ApiError(403, CUSTOMER_TYPE_NOT_ALLOWED) when the caller is not a
 * buyer (Q8 gate), ApiError(404) for a store_id that isn't the caller's own,
 * ApiError(422) for a blank mid_number or a missing store name/ZIP when no
 * store is chosen, and ApiError(409) only as a race backstop — a live
 * number is otherwise answered as "queued" (the conflict is cloaked from
 * the requester; only the operator's audit trail records it).
 */
export async function claimMID(req: ClaimMIDRequest): Promise<ClaimMIDResult> {
  const body: ClaimMIDRequest = { mid_number: req.mid_number.trim() };
  if (req.store_id) body.store_id = req.store_id;
  if (req.processor_name?.trim()) body.processor_name = req.processor_name.trim();
  if (req.dba_name?.trim()) body.dba_name = req.dba_name.trim();
  if (req.opened_on) body.opened_on = req.opened_on;
  if (req.requested_store_name?.trim()) body.requested_store_name = req.requested_store_name.trim();
  if (req.requested_line1?.trim()) body.requested_line1 = req.requested_line1.trim();
  if (req.requested_city?.trim()) body.requested_city = req.requested_city.trim();
  if (req.requested_state?.trim()) body.requested_state = req.requested_state.trim();
  if (req.requested_postal_code?.trim()) body.requested_postal_code = req.requested_postal_code.trim();

  const data = await embedApiFetch<{ already_linked?: boolean; queued?: boolean }>(MIDS_PATH, {
    method: "POST",
    body,
  });
  // The server answers exactly one of the two keys; a payload with neither
  // (a defensive default, never observed) reads as queued — the panel then
  // re-reads the list, which is the honest derivation either way.
  if (data?.already_linked) return { outcome: "already_linked" };
  return { outcome: "queued" };
}
